import type {
    AppliedHeuristicLogMessage,
    ConversionAlgoConfig,
    ConversionDal,
    IStageRunner,
    MobileAlgoContext,
    Predicate,
    ReadOnlyExtensionAPI,
    StageRuntimeStore,
    Stages
} from '../types'
import type {SnapshotDal} from '@wix/document-manager-core'
import {AlgorithmModes, SkipPredicate, StageName} from '../enums'
import _ from 'lodash'
import {isZeroHeightOrEmptySection} from '../plugins/common'
import {getStructurePointer} from '../conversionDal/utils/pointer'

const stageOrder: StageName[] = [
    StageName.COMPONENT_CATEGORIZATION,
    StageName.GROUPING,
    StageName.GROUPING_ORDER,
    StageName.STRUCTURE_ORDER,
    StageName.INIT_CSS_MODELS,
    StageName.POSITION,
    StageName.HORIZONTAL_LAYOUT_SCALE,
    StageName.VERTICAL_LAYOUT_SCALE,
    StageName.TRANSFORM_STYLE,
    StageName.POST_PROCESS
]

const initSkipPredicates = (
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    conversionDal: ConversionDal,
    config: ConversionAlgoConfig
) => ({
    [SkipPredicate.INVALID_FOR_GROUPING]: () =>
        config.mode === AlgorithmModes.Adaptive ||
        readOnlyExtensionAPI.components.isPinned(config.rootPointer) ||
        readOnlyExtensionAPI.components.isHeaderSection(config.rootPointer) ||
        isZeroHeightOrEmptySection({componentPointer: getStructurePointer(config.rootPointer), conversionDal})
})

const initSubscriptions = () => ({
    [StageName.COMPONENT_CATEGORIZATION]: [],
    [StageName.GROUPING]: [SkipPredicate.INVALID_FOR_GROUPING],
    [StageName.GROUPING_ORDER]: [],
    [StageName.STRUCTURE_ORDER]: [],
    [StageName.INIT_CSS_MODELS]: [SkipPredicate.SHOULD_HALT_ALGO],
    [StageName.POSITION]: [SkipPredicate.SHOULD_HALT_ALGO],
    [StageName.HORIZONTAL_LAYOUT_SCALE]: [SkipPredicate.SHOULD_HALT_ALGO],
    [StageName.VERTICAL_LAYOUT_SCALE]: [SkipPredicate.SHOULD_HALT_ALGO],
    [StageName.TRANSFORM_STYLE]: [SkipPredicate.SHOULD_HALT_ALGO],
    [StageName.POST_PROCESS]: [SkipPredicate.SHOULD_HALT_ALGO]
})

export class StageRunner implements IStageRunner {
    private predicates: Partial<Record<SkipPredicate, Predicate>> = {}
    private subscriptions: Record<StageName, SkipPredicate[]> = initSubscriptions()

    constructor(
        private stages: Stages,
        readOnlyExtensionAPI: ReadOnlyExtensionAPI,
        conversionDal: ConversionDal,
        config: ConversionAlgoConfig
    ) {
        this.predicates = initSkipPredicates(readOnlyExtensionAPI, conversionDal, config)
    }

    async run(
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        pageId: string,
        config: ConversionAlgoConfig,
        logger?: AppliedHeuristicLogMessage[]
    ) {
        for (const stageName of stageOrder) {
            if (await this.shouldSkipStage(stageName)) {
                continue
            }

            const stage = this.stages[stageName]
            await stage.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                config,
                this.getRuntimeStore(stageName),
                logger
            )
            conversionDal.commitChanges(stageName)
        }

        this.flush()
    }

    private subscribeToSkipPredicate(stageName: StageName, key: SkipPredicate) {
        this.subscriptions[stageName].push(key)
    }

    private getRuntimeStore(stageName: StageName): StageRuntimeStore {
        return {
            registerSkipPredicate: this.registerSkipPredicate.bind(this),
            subscribeToSkipPredicate: _.partial(this.subscribeToSkipPredicate.bind(this), stageName)
        }
    }

    private flush() {
        this.predicates = {}
        this.subscriptions = initSubscriptions()
    }

    private registerSkipPredicate(key: SkipPredicate, predicate: Predicate) {
        this.predicates[key] = predicate
    }

    private async shouldSkipStage(stageName: StageName): Promise<boolean> {
        const skipPredicates = this.subscriptions[stageName]
        return (
            await Promise.all(
                skipPredicates.map(async (key: SkipPredicate) => {
                    return (await this.predicates[key]?.()) ?? false
                })
            )
        ).some(Boolean)
    }
}
